import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { StrategicContextPicker, TaskItemAutocomplete } from "src/components";
import { TaskItemContextType } from "src/constants";
import { TaskItemDetailDto, useEditTaskItemContext, useEditTaskItemParent } from "src/http";

interface Props {
  taskItem: TaskItemDetailDto;
  open: boolean;
  onDone: () => void;
  onCancel: () => void;
}

const EditTaskItemContextModal = (props: Props) => {
  const [contextType, setContextType] = useState<string>(TaskItemContextType.StrategicItem);
  const [selectedContextActionItemGuid, setSelectedContextActionItemGuid] = useState<string | null>(null);
  const [selectedParentTaskItemGuid, setSelectedParentTaskItemGuid] = useState<string | null>(null);
  const { mutate: editTaskItemContext, isLoading: isEditingContext } = useEditTaskItemContext();
  const { mutate: editTaskItemParent, isLoading: isEditingParent } = useEditTaskItemParent();

  const buttonsDisabled = isEditingContext || isEditingParent;

  useEffect(() => {
    setContextType(props.taskItem.parentTaskItem ? TaskItemContextType.Task : TaskItemContextType.StrategicItem);
    setSelectedContextActionItemGuid(props.taskItem.context?.actionItemGuid ?? null);
    setSelectedParentTaskItemGuid(props.taskItem.parentTaskItem?.taskItemGuid ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const defaultContext = useMemo(() => {
    if (props.taskItem.context) {
      return {
        teamSlug: props.taskItem.context.team!.slug,
        actionItemGuid: props.taskItem.context.actionItemGuid,
      };
    } else {
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  function handleContextTypeChanged(_event: React.ChangeEvent<HTMLInputElement>, value: string) {
    setContextType(value);
  }

  function handleParentTaskItemChanged(taskItemGuid: string | null) {
    setSelectedParentTaskItemGuid(taskItemGuid);
  }

  function handleDoneClicked() {
    if (contextType === TaskItemContextType.StrategicItem) {
      editTaskItemContext({
        taskItemGuid: props.taskItem.guid,
        contextActionItemGuid: selectedContextActionItemGuid,
      });
    }

    if (contextType === TaskItemContextType.Task) {
      editTaskItemParent({
        taskItemGuid: props.taskItem.guid,
        parentTaskItemGuid: selectedParentTaskItemGuid,
        teamSlug: props.taskItem.team?.slug,
        ownerUserGuids: props.taskItem.owners.map((x) => x.user.userId),
      });
    }

    props.onDone();
  }

  function handleCancelClicked() {
    props.onCancel();
  }

  return (
    <Dialog open={props.open} onClose={handleCancelClicked}>
      <DialogTitle>Connect to Context</DialogTitle>
      <DialogContent>
        <Stack spacing={0.5}>
          <RadioGroup value={contextType} onChange={handleContextTypeChanged}>
            <FormControlLabel
              value={TaskItemContextType.StrategicItem}
              control={<Radio />}
              label="Connect to Strategic Item"
            />
            <FormControlLabel value={TaskItemContextType.Task} control={<Radio />} label="Connect to Task" />
          </RadioGroup>

          <Divider variant="dashed" />

          {contextType === TaskItemContextType.StrategicItem && (
            <StrategicContextPicker
              disabled={isEditingContext}
              defaultContext={defaultContext}
              onSelect={(selectedStrategicItem) => {
                setSelectedContextActionItemGuid(selectedStrategicItem?.actionItemGuid ?? null);
              }}
            />
          )}

          {contextType === TaskItemContextType.Task && (
            <TaskItemAutocomplete
              contextTaskItemGuid={props.taskItem.guid}
              value={selectedParentTaskItemGuid}
              onChange={handleParentTaskItemChanged}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={buttonsDisabled} onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button variant="contained" disabled={buttonsDisabled} onClick={handleDoneClicked}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { EditTaskItemContextModal };
